import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';

import { addTime } from 'modules/elapsedTime/actions';
import { finalizeSelfService } from 'modules/selfServices/actions';

import { isPublicDevice } from 'modules/dealers/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { isLogoutModalDisplayed } from 'modules/sockets/selectors';
import { getStepsConfig } from 'modules/selfServices/stepsConfigSelectors';
import { isCheckinSelfService } from 'modules/selfServices/selectors';

import { useDispatch } from 'hooks';
import useCurrentStep from 'hooks/useCurrentStep';
import { getUrlParam } from 'utils/urlUtils';
import socketSlice from 'modules/sockets/reducer';
import useElapsedTime from 'hooks/useElapsedTime';
import { toUrlSlug, fromUrlSlug } from 'utils/stepUrlUtils';
import { usePatchDropOffPointMutation } from 'modules/selfServices/service';
import { useGetSelfServiceByIdQuery } from 'modules/selfServices/service';
import { INACTIVITY_SELF_SERVICE_FLOWS, INACTIVITY_TIME_ON_PAYMENT } from 'modules/auth/constants';

import { Spinner } from 'components/ui';
import { isOpened } from 'modules/kiosk/selectors';
import { createDictionary } from 'utils/objectUtils';
import SocketRedirectModal from 'components/SocketRedirectModal';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';
import { upsertFormDebounce } from 'components/ui/ConnectedForm/decorator';

import Inactivity from './Inactivity';
import type { SelfServiceStep, SelfServiceOutletContext } from './types';
import UpdateLogoutModal from '../UpdateLogoutModal';
import { getSelfServiceSteps } from './Steps';
import { isNotAnswered } from './Steps/utils';

const STEPS_TO_FINALIZE_AT_INIT = [SelfServiceSteps.PARTTEAM_KEY, SelfServiceSteps.SHAREBOX_KEY, SelfServiceSteps.FINAL_INSTRUCTIONS];

const SelfServiceLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { step: stepSlug } = useParams<{ step: string }>();
  const dispatch = useDispatch();
  const { getElapsedTime, reset } = useElapsedTime();

  const config = useSelector(getStepsConfig);

  const currentStep = useCurrentStep();
  const doorsOpened = useSelector(isOpened);
  const isPublic = useSelector(isPublicDevice);
  const isCheckin = useSelector(isCheckinSelfService);
  const displayModal = useSelector(isLogoutModalDisplayed);
  const selfServiceId = useSelector(getSelectedSelfServiceId);

  const {
    data, isLoading, isSuccess, isError, isFetching,
  } = useGetSelfServiceByIdQuery({ id: selfServiceId });
  const [patchDropOffPoint] = usePatchDropOffPointMutation();

  const displayLoading = isLoading || isFetching;

  const adjustedConfig = React.useMemo(() => {
    if (stepSlug && fromUrlSlug(stepSlug) === SelfServiceSteps.BASKET) {
      return { ...config, displayBasket: true };
    }
    return config;
  }, [config, stepSlug]);

  const steps = React.useMemo(() => (
    data ? getSelfServiceSteps(data, adjustedConfig) : []
  ), [data, adjustedConfig]);

  const setDropOffPoint = React.useCallback((key: string) => {
    if (key) {
      patchDropOffPoint({ step: key, selfServiceId });
    }
  }, [patchDropOffPoint, selfServiceId]);

  // Redirect to first step if no step in URL or if current step is not visible
  React.useEffect(() => {
    if (steps.length > 0) {
      const isStepVisible = stepSlug && steps.some(({ key }) => key === fromUrlSlug(stepSlug));
      if (!isStepVisible) {
        navigate(`${toUrlSlug(steps[0].key)}${location.search}`, {
          replace: true,
          state: location.state,
        });
      }
    }
  }, [steps, stepSlug, navigate, location.search, location.state]);

  // Auto-finalize on init for specific steps
  const initialStepRef = React.useRef<SelfServiceStep | null>(null);
  React.useEffect(() => {
    if (data && steps.length > 0 && !initialStepRef.current) {
      const initialStep = steps[0];
      initialStepRef.current = initialStep;

      if (isNotAnswered(data.status) && STEPS_TO_FINALIZE_AT_INIT.includes(initialStep.key)) {
        dispatch(finalizeSelfService(initialStep.key, getElapsedTime()));
      }
    }
  }, [data, steps, dispatch, getElapsedTime]);

  const handleStepChange = React.useCallback((newStep: SelfServiceStep) => {
    window.scrollTo(0, 1); // Force scroll to top on step change
    upsertFormDebounce.flush();
    setDropOffPoint(newStep.apiKey);

    const prevStep = steps.find(({ key }) => key === newStep.previous);
    const shouldFinalizeSelfService = prevStep?.data?.isFinalizeStep ?? false;

    if (isCheckin && newStep.key === 'FINAL_INSTRUCTIONS') {
      dispatch(socketSlice.actions.closeSocket());
    }

    if (shouldFinalizeSelfService) {
      dispatch(finalizeSelfService(newStep.key, getElapsedTime()));
    } else {
      dispatch(addTime({ key: newStep.key, time: getElapsedTime() }));
    }
    reset();
  }, [setDropOffPoint, steps, isCheckin, reset, dispatch, getElapsedTime]);

  const shouldUseInactivity = isPublic || currentStep === SelfServiceSteps.FINAL_INVOICE;
  const isInactivityPaused = currentStep === SelfServiceSteps.PARTTEAM_KEY && doorsOpened;
  const inactivitySecondsByStep = createDictionary<string, number>({
    [SelfServiceSteps.FINAL_INVOICE]: INACTIVITY_TIME_ON_PAYMENT,
  }, INACTIVITY_SELF_SERVICE_FLOWS);

  if (!selfServiceId || isError) {
    const pathToken = getUrlParam('token');
    return (<Navigate to={`/?token=${pathToken}`} />);
  }

  const outletContext: SelfServiceOutletContext = {
    steps,
    onStepChange: handleStepChange,
  };

  return (
    <div>
      {displayLoading && <Spinner className="homePage" />}

      {!displayLoading && isSuccess && steps.length > 0 && stepSlug && (
        <Outlet context={outletContext} />
      )}

      <SocketRedirectModal />
      {displayModal && <UpdateLogoutModal />}
      {shouldUseInactivity && <Inactivity seconds={inactivitySecondsByStep[currentStep]} paused={isInactivityPaused} />}
    </div>
  );
};

export default SelfServiceLayout;
